import { useEffect, useRef } from 'react';
import { ChevronDown, ArrowRight } from 'lucide-react';

export default function Hero() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (el) el.classList.add('visible');
  }, []);

  const scrollToAbout = (e) => {
    e.preventDefault();
    const el = document.querySelector('#about');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: 'linear-gradient(165deg, #f7f6f3 0%, #ede9e3 40%, #f0ece6 70%, #f7f6f3 100%)' }}
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1761047726555-9fdd6f2c51c9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=2000"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-[0.35]"
          style={{ filter: 'saturate(0.7) brightness(1.08)', objectPosition: 'center 70%' }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 65% 55% at 50% 42%, rgba(247,246,243,0.72) 0%, rgba(247,246,243,0.15) 100%)' }}
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, #f7f6f3 0%, transparent 12%, transparent 88%, #f7f6f3 100%)' }}
        />
      </div>

      {/* Decorative elements */}
      <div className="absolute inset-0 z-[1] overflow-hidden pointer-events-none">
        <div
          className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, #1a7a7a 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-48 -left-48 w-[500px] h-[500px] rounded-full opacity-[0.03]"
          style={{ background: 'radial-gradient(circle, #d4af37 0%, transparent 70%)' }}
        />
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent 10%, #d4af37 50%, transparent 90%)' }} />
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: 'linear-gradient(rgba(26,122,122,1) 1px, transparent 1px), linear-gradient(90deg, rgba(26,122,122,1) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-12 h-[1px] bg-[#d4af37]" />
            <p
              className="text-xs md:text-sm uppercase tracking-[0.35em] text-[#1a7a7a] font-semibold"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Global Citizen Consulting
            </p>
            <div className="w-12 h-[1px] bg-[#d4af37]" />
          </div>

          <h1
            className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.08] mb-8"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            <span className="text-[#1a2332]">Jurisdictional Strategy</span>
            <br />
            <span className="text-[#1a2332]">for Lives That</span>
            <br />
            <span className="text-[#1a7a7a]">Cross Borders</span>
          </h1>

          <div className="flex justify-center mb-8">
            <div className="w-20 h-[1px] bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />
          </div>

          <p
            className="text-base md:text-lg text-[#5a6370] max-w-2xl mx-auto mb-12 leading-relaxed"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Independent counsel for founders, executives, and family offices. Retained for the long view.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://calendly.com/chr-globalcitizen/video-consultation-clone"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-primary"
              className="group bg-[#1a7a7a] text-white font-semibold px-10 py-4 rounded-full text-base hover:bg-[#1a6868] transition-all duration-300 inline-flex items-center justify-center gap-2 shadow-lg shadow-[#1a7a7a]/15 hover:shadow-xl hover:shadow-[#1a7a7a]/20"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Request an Introduction
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#about"
              data-testid="hero-cta-secondary"
              className="border border-[#c9b88c] text-[#1a2332] hover:bg-[#1a2332] hover:text-white hover:border-[#1a2332] font-semibold px-10 py-4 rounded-full text-base transition-all duration-300 inline-block"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              onClick={scrollToAbout}
            >
              The Firm
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <button
          data-testid="scroll-indicator"
          onClick={scrollToAbout}
          className="p-3 text-[#c9b88c] hover:text-[#1a7a7a] transition-colors scroll-indicator-float"
        >
          <ChevronDown size={28} />
        </button>
      </div>
    </section>
  );
}
